import React, { useState, useEffect, useRef } from 'react';
import { SharedTable } from '../common';

const EmployeesTableWithShared = ({
  employees,
  selectedEmployees,
  setSelectedEmployees,
  selectAll,
  setSelectAll,
  handleSelectAll,
  setSelectedEmployee,
  setViewModalOpen,
  formatLastActive,
  pagination,
  onPageChange,
  onLimitChange, // Add onLimitChange prop here
  sortBy,
  sortOrder,
  handleSort,
  showColumnModal,
  setShowColumnModal,
  loading = false
}) => {
  // Column configuration with default order and width
  const defaultColumns = [
    { id: 'nameEmail', label: 'Name/Email', visible: true, width: 250 },
    { id: 'roleId', label: 'Role/ID', visible: true, width: 150 },
    { id: 'country', label: 'Country', visible: true, width: 130 },
    { id: 'lastActive', label: 'Last Active', visible: true, width: 150, sortable: true }
  ];

  // Load column order from localStorage or use default
  const [columns, setColumns] = useState(() => {
    const savedColumns = localStorage.getItem('employeesTableColumns');
    return savedColumns ? JSON.parse(savedColumns) : defaultColumns;
  });

  // Keep a reference to default columns for reset functionality
  const defaultColumnsRef = useRef(defaultColumns);

  // Save column configuration to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('employeesTableColumns', JSON.stringify(columns));
  }, [columns]);
  
  // Handle reset to default
  const handleResetColumns = () => {
    setColumns([...defaultColumnsRef.current]);
  };

  // Handle individual employee selection
  const handleEmployeeSelection = (employeeId) => {
    if (selectedEmployees.includes(employeeId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== employeeId));
      setSelectAll(false);
    } else {
      setSelectedEmployees([...selectedEmployees, employeeId]);
      if (selectedEmployees.length + 1 === employees.length) {
        setSelectAll(true);
      }
    }
  };

  // Render cell content based on column ID
  const renderCell = (employee, columnId) => {
    switch (columnId) {
      case 'nameEmail':
        return (
          <div className="flex items-center">
            {employee.profile_image ? (
              <img className="h-10 w-10 rounded-full mr-3" src={employee.profile_image} alt="" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                <span className="text-blue-800 font-medium text-lg">
                  {employee.name ? employee.name.charAt(0).toUpperCase() : "?"}
                </span>
              </div>
            )}
            <div>
              <div className="text-sm font-medium text-gray-900">
                {employee.name}
              </div>
              <div className="text-sm text-gray-500">
                {employee.email}
              </div>
            </div>
          </div>
        );
      case 'roleId':
        return (
          <div>
            <div className="text-sm text-gray-900">
              {(() => {
                // Check for role_name first (from new API), then fall back to role (from old API)
                const role = employee.role_name ? employee.role_name.toLowerCase() : 
                            employee.role ? employee.role.toLowerCase() : '';
                
                if (role === 'admin') {
                  return (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                      admin
                    </span>
                  );
                } else if (role === 'tutor') {
                  return (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      tutor
                    </span>
                  );
                } else if (role === 'manager') {
                  return (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      manager
                    </span>
                  );
                } else if (role === 'sales') {
                  return (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      sales
                    </span>
                  );
                } else if (role === 'superuser') {
                  return (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      superuser
                    </span>
                  );
                } else {
                  // For custom roles, display the role_name or fallback to role or 'user'
                  return (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {employee.role_name || employee.role || 'user'}
                    </span>
                  );
                }
              })()}
            </div>
            <div className="text-sm text-gray-500">
              ID: {employee.id}
            </div>
          </div>
        );
      case 'country':
        return (
          <div className="text-sm text-gray-900">{employee.country || '—'}</div>
        );
      case 'lastActive':
        return (
          <div className="text-sm text-gray-500">
            {formatLastActive(employee.last_active)}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <SharedTable
      data={employees}
      columns={columns}
      selectedItems={selectedEmployees}
      onSelectItem={handleEmployeeSelection}
      selectAll={selectAll}
      onSelectAll={handleSelectAll}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={handleSort}
      renderCell={renderCell}
      loading={loading}
      emptyMessage="No employees found. Create your first employee to get started."
      resizable={true}
      onColumnsChange={setColumns}
      showColumnManagement={showColumnModal}
      onToggleColumnManagement={setShowColumnModal}
      pagination={pagination}
      onPageChange={onPageChange}
      onLimitChange={onLimitChange} // Pass onLimitChange down to SharedTable
      onResetColumns={handleResetColumns}
      tableHeight="calc(100vh - 250px)"
      onRowClick={(employee) => {
        setSelectedEmployee(employee);
        setViewModalOpen(true);
      }}
    />
  );
};

export default EmployeesTableWithShared;
